import React from "react";
import { stripHtmlTags, takeFirst } from "~/features/app/utils";
import { Media } from "~/features/ui/media";
import { useFragment, type FragmentType } from "~/gql/runtime";
import { ArticleTags } from "~/features/article/article-tags";
import { EditorialCard } from "~/features/card/editorial-card";
import { EditorialFeaturedCard } from "~/features/card/editorial-featured-card";
import { graphql } from "~/gql/runtime";
import { Asset } from "~/features/craft/asset";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { up } from "~/features/app/screens";

const fragment = graphql(`
  fragment ArticleCard on article_default_Entry {
    id
    headline: title
    url
    legacyImage: articleLegacyImage {
      ...Asset
    }
    hero {
      ... on hero_editorialHero_BlockType {
        text: baseRichText
        media: baseMedia {
          ...Media
        }
      }
    }
    ...ArticleMetaDataTags
  }
`);

export type ArticleCardViewProps = {
  featured?: boolean;
  size?: "regular" | "large";
  ratio?: "3/2" | "16/9" | "fill";
  headline?: string | null;
  text?: string | null;
  url?: string | null;
  media?: React.ReactNode;
  tags?: React.ReactNode;
};

export function ArticleCardView(props: ArticleCardViewProps) {
  const { featured, size = "regular", headline, text, url, media, tags } = props;

  const Card = featured ? EditorialFeaturedCard : EditorialCard;

  return (
    <Card
      headline={headline ?? undefined}
      text={stripHtmlTags(text ?? "")}
      url={url ?? undefined}
      linkText="Read More"
      size={size}
      media={media}
      tags={tags}
    />
  );
}

type Props = FragmentType<typeof fragment> & {
  featured?: boolean;
  size?: "regular" | "large";
  ratio?: "3/2" | "16/9" | "fill";
};

export function ArticleCard(props: Props) {
  const { featured, size = "regular", ratio: _ratio = "3/2" } = props;
  const { headline, hero, legacyImage, url, ...rest } = useFragment(fragment, props);
  const { text, media } = takeFirst(hero) || {};

  const isLg = useMediaQuery(up("lg"));

  const ratio = isLg ? (_ratio === "fill" ? undefined : _ratio) : "3/2";
  const fit = isLg ? (ratio ? "fill" : "cover") : "fill";

  return (
    <ArticleCardView
      featured={featured}
      size={size}
      headline={headline}
      text={text}
      url={url}
      media={
        media ? (
          <Media
            {...takeFirst(media)}
            imageProps={{ ratio, fit, sizes: "lg:33vw", imgixProps: { q: 60 } }}
            externalVideoProps={{ ratio, fit }}
            inlineVideoProps={{ ratio, fit }}
          />
        ) : (
          <Asset
            asset={takeFirst(legacyImage)}
            imageProps={{
              ratio,
              fit,
              sizes: "lg:33vw",
              imgixProps: { q: 60 },
            }}
            externalVideoProps={{ ratio, fit }}
            inlineVideoProps={{ ratio, fit }}
          />
        )
      }
      tags={<ArticleTags {...rest} tagVariant="blur" />}
    />
  );
}
